import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RegressionScenario } from "./regression.types";
import { regressionStatusIcon } from "./regressionStatus";

interface Props {
  scenarios: RegressionScenario[];
}

const fmtMs = (ms: number) => (ms >= 60000
  ? `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
  : ms >= 1000
    ? `${(ms / 1000).toFixed(1)}s`
    : `${ms}ms`);

export const RegressionFeatureAccordion: React.FC<Props> = ({ scenarios }) => {
  const features = useMemo(() => {
    const byPath = new Map<string, { featureName: string; scenarios: RegressionScenario[] }>();
    for (const s of scenarios) {
      const entry = byPath.get(s.featurePath) ?? { featureName: s.featureName, scenarios: [] };
      entry.scenarios.push(s);
      byPath.set(s.featurePath, entry);
    }
    return Array.from(byPath.entries()).map(([featurePath, entry]) => ({ featurePath, ...entry }));
  }, [scenarios]);

  if (features.length === 0) {
    return <Typography variant="body2" color="text.secondary">No scenario data in the latest run.</Typography>;
  }

  return (
    <Box>
      {features.map(({ featurePath, featureName, scenarios: featureScenarios }) => {
        const passed = featureScenarios.filter((s) => s.result === "SUCCESS").length;
        const total = featureScenarios.length;
        const durationMs = featureScenarios.reduce((sum, s) => sum + s.durationMs, 0);
        return (
          <Accordion key={featurePath} disableGutters variant="outlined" sx={{ "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} variant="body2">{featureName}</Typography>
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">{featurePath}</Typography>
                </Box>
                <Chip label={`${passed}/${total}`} size="small" color={passed === total ? "success" : "error"} />
                <Typography variant="caption" color="text.secondary" sx={{ width: 56, textAlign: "right" }}>
                  {fmtMs(durationMs)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Table size="small">
                <TableBody>
                  {featureScenarios.map((s) => (
                    <TableRow key={s.scenarioId}>
                      <TableCell width={28}>{regressionStatusIcon(s.result)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{s.name}</Typography>
                      </TableCell>
                      <TableCell align="right" width={70}>
                        <Typography variant="caption" color="text.secondary">{fmtMs(s.durationMs)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default RegressionFeatureAccordion;
